/**
 * Videospeler met eigen knoppen, voor een Vimeo-film én voor een eigen bestand.
 *
 * Waarom niet de speler van de bron zelf: Vimeo mag een video die uit zichzelf begint
 * alleen zonder geluid starten en plakt er dan een 'Unmute'-balk boven -- via de Player
 * API mag het geluid wél meteen aan, zolang het afspelen uit een klik komt. En de
 * `controls` van de browser zijn per browser anders en hebben niets met de huisstijl te
 * maken. Beide gevallen krijgen daarom dezelfde knoppen.
 *
 * De opbouw: alles wat met de knoppen te maken heeft staat één keer in setupPlayer(). Wat
 * per bron verschilt zit in een aandrijving -- een klein object met play, pause, seek,
 * volume en duration, dat bij elke verandering `emit()` aanroept. Zo weet de rest van dit
 * bestand niet of er een iframe of een <video> onder zit, en kost een derde bron alleen
 * een derde aandrijving.
 *
 * De API van Vimeo wordt pas opgehaald als er echt een Vimeo-speler in beeld komt, en
 * maar één keer per pagina.
 */

const API = 'https://player.vimeo.com/api/player.js';

let apiPromise = null;

/**
 * Haalt de Vimeo Player API op. Meerdere spelers delen dezelfde belofte.
 *
 * @return {Promise}
 */
function loadApi() {
  if (window.Vimeo && window.Vimeo.Player) {
    return Promise.resolve(window.Vimeo);
  }

  if (! apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = API;
      script.async = true;
      script.onload = () => resolve(window.Vimeo);
      script.onerror = () => {
        apiPromise = null;
        reject(new Error('Vimeo API niet geladen'));
      };

      document.head.appendChild(script);
    });
  }

  return apiPromise;
}

/**
 * De aandrijving voor een Vimeo-film.
 *
 * @param {Element} root
 * @param {Function} emit
 * @return {Promise<object>|null}
 */
function vimeoEngine(root, emit) {
  const id = root.dataset.playerVimeoId;
  const iframe = root.querySelector('.player__iframe');

  if (! id || ! iframe) {
    return null;
  }

  // background=1 haalt Vimeo's eigen knoppen weg; die van ons komen ervoor in de plaats.
  iframe.src = `https://player.vimeo.com/video/${id}?background=1&autoplay=0&loop=0&muted=0&api=1`;

  return loadApi().then((Vimeo) => {
    const player = new Vimeo.Player(iframe);

    player.on('play', () => emit('play'));
    player.on('pause', () => emit('pause'));
    player.on('bufferstart', () => emit('loading'));
    player.on('bufferend', () => emit('resume'));
    player.on('ended', () => {
      emit('ended');
      player.setCurrentTime(0).catch(noop);
    });
    player.on('timeupdate', (data) => emit('time', data.seconds, data.duration));

    return player.getDuration()
      .then((value) => emit('time', 0, value))
      .catch(noop)
      .then(() => ({
        play: () => player.play(),
        pause: () => player.pause().catch(noop),
        seek: (seconds) => player.setCurrentTime(seconds).catch(noop),
        volume: (value) => player.setVolume(value),
        rewind: () => player.setCurrentTime(0).catch(noop),
      }));
  });
}

/**
 * De aandrijving voor een eigen bestand.
 *
 * De browser doet hier het meeste werk al; dit vertaalt alleen zijn gebeurtenissen naar
 * dezelfde meldingen als de Vimeo-kant.
 *
 * @param {Element} root
 * @param {Function} emit
 * @return {Promise<object>|null}
 */
function fileEngine(root, emit) {
  // Op het haakje en niet op de klasse: het videoblok dat tijdens het scrollen groeit
  // hangt deze aandrijving aan een <video> die het zelf al op de pagina had staan, en dat
  // element heeft zijn eigen klassen.
  const video = root.querySelector('[data-player-video]');

  if (! video) {
    return null;
  }

  video.addEventListener('play', () => emit('play'));
  video.addEventListener('pause', () => emit('pause'));
  video.addEventListener('waiting', () => emit('loading'));
  video.addEventListener('playing', () => emit('resume'));
  video.addEventListener('ended', () => {
    emit('ended');
    video.currentTime = 0;
  });
  video.addEventListener('timeupdate', () => emit('time', video.currentTime, video.duration));
  video.addEventListener('loadedmetadata', () => emit('time', 0, video.duration));

  return Promise.resolve({
    play: () => video.play(),
    pause: () => {
      video.pause();

      return Promise.resolve();
    },
    seek: (seconds) => {
      video.currentTime = seconds;

      return Promise.resolve();
    },
    // Het geluid via `muted` en niet via `volume`: alleen `muted` telt mee in de
    // afweging van de browser of een video mag beginnen.
    volume: (value) => {
      video.muted = value === 0;
      video.volume = value;

      return Promise.resolve();
    },
    rewind: () => {
      video.currentTime = 0;

      return Promise.resolve();
    },
  });
}

/**
 * Zet één speler op. Geeft een handvat terug waarmee de omgeving hem kan starten en
 * stoppen -- de showreel gebruikt dat bij het openen en sluiten.
 *
 * @param {Element} root
 * @return {{play: Function, pause: Function, stop: Function}|null}
 */
export function setupPlayer(root) {
  if (! root || root.dataset.playerReady === 'true') {
    return null;
  }

  const toggle = root.querySelector('[data-player-control="playpause"]');
  const toggleLabel = root.querySelector('[data-player-toggle-label]');
  const scrub = root.querySelector('[data-player-control="timeline"]');
  const progress = root.querySelector('[data-player-progress]');
  const time = root.querySelector('[data-player-time]');
  const mute = root.querySelector('[data-player-control="mute"]');
  const muteLabel = root.querySelector('[data-player-mute-label]');
  const fullscreen = root.querySelector('[data-player-control="fullscreen"]');

  let duration = 0;
  let scrubbing = false;
  let wanted = false;
  let engine = null;

  const start = root.dataset.playerKind === 'file'
    ? fileEngine(root, emit)
    : vimeoEngine(root, emit);

  if (! start) {
    return null;
  }

  root.dataset.playerReady = 'true';

  const ready = start
    .then((made) => {
      engine = made;
    })
    .catch(() => {
      // Zonder aandrijving valt er niets te bedienen; dan liever de eigen knoppen weg dan
      // knoppen die niets doen.
      root.dataset.playerState = 'error';
    });

  if (toggle) {
    toggle.addEventListener('click', () => (wanted ? pause() : play()));
  }

  if (scrub) {
    scrub.addEventListener('input', () => {
      scrubbing = true;
      paint(Number(scrub.value));
    });

    scrub.addEventListener('change', () => {
      scrubbing = false;

      if (engine) {
        engine.seek(Number(scrub.value));
      }
    });
  }

  if (mute) {
    mute.addEventListener('click', () => {
      const off = root.dataset.playerMuted !== 'true';

      root.dataset.playerMuted = off ? 'true' : 'false';

      if (muteLabel) {
        muteLabel.textContent = off ? 'Geluid aan' : 'Geluid uit';
      }

      if (engine) {
        engine.volume(off ? 0 : 1);
      }
    });
  }

  if (fullscreen) {
    fullscreen.addEventListener('click', () => {
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(noop);
      } else if (root.requestFullscreen) {
        root.requestFullscreen().catch(noop);
      }
    });

    document.addEventListener('fullscreenchange', () => {
      root.dataset.playerFullscreen = document.fullscreenElement === root ? 'true' : 'false';
    });
  }

  /**
   * Wat de aandrijving meldt, vertaald naar de stand van de knoppen. Eén trechter, zodat
   * beide bronnen hetzelfde gedrag opleveren.
   *
   * 'resume' is het einde van het bufferen en niet zomaar 'playing': de bezoeker kan
   * tijdens het wachten op pauze hebben gedrukt, en dan hoort de knop niet alsnog op
   * spelen te springen.
   */
  function emit(event, seconds, total) {
    if (event === 'time') {
      duration = total || duration;

      if (! scrubbing) {
        set(seconds);
      }

      return;
    }

    if (event === 'resume') {
      state(wanted ? 'playing' : 'paused');

      return;
    }

    state({ play: 'playing', ended: 'idle', loading: 'loading' }[event] || 'paused');
  }

  function state(value) {
    root.dataset.playerState = value;

    if (toggleLabel) {
      toggleLabel.textContent = value === 'playing' ? 'Pauzeren' : 'Afspelen';
    }
  }

  /**
   * De tijdlijn en de klok bijwerken.
   */
  function set(seconds) {
    if (scrub) {
      scrub.max = duration || 100;
      scrub.value = seconds;
    }

    paint(seconds);
  }

  function paint(seconds) {
    if (progress) {
      progress.max = duration || 100;
      progress.value = seconds;
    }

    if (time) {
      time.textContent = clock(seconds);
    }
  }

  function play() {
    wanted = true;
    state('loading');

    // Het geluid staat aan tenzij de bezoeker het zelf heeft uitgezet. Dat mag hier,
    // omdat deze functie altijd uit een klik komt.
    return ready.then(() => {
      if (! engine) {
        return null;
      }

      return Promise.resolve(engine.volume(root.dataset.playerMuted === 'true' ? 0 : 1))
        .catch(noop)
        .then(() => engine.play())
        .catch(() => {
          // Weigert de browser het alsnog, dan liever zonder geluid dan niets.
          root.dataset.playerMuted = 'true';

          return Promise.resolve(engine.volume(0)).then(() => engine.play()).catch(noop);
        });
    });
  }

  function pause() {
    wanted = false;

    return ready.then(() => (engine ? Promise.resolve(engine.pause()).catch(noop) : null));
  }

  /**
   * Terug naar de beginstand: stil, vooraan, en het stilstaande beeld weer erover.
   */
  function stop() {
    wanted = false;

    return ready.then(() => {
      if (! engine) {
        return null;
      }

      return Promise.resolve(engine.pause())
        .then(() => engine.rewind())
        .catch(noop)
        .then(() => state('idle'));
    });
  }

  return { play, pause, stop };
}

/**
 * Zet elke speler op de pagina op. Spelers met [data-player-defer] slaat hij over: die
 * worden door hun eigen onderdeel opgezet, op het moment dat ze in beeld komen.
 */
export default function initPlayer() {
  document
    .querySelectorAll('[data-player-init]:not([data-player-defer])')
    .forEach(setupPlayer);
}

function clock(seconds) {
  const total = Math.max(0, Math.floor(seconds || 0));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;

  return `${minutes}:${rest < 10 ? '0' : ''}${rest}`;
}

function noop() {}
